// Lightweight components — loaded synchronously (no heavy deps)
import { initTabs } from './ui-components/tabs';
import { initializeAccordion } from './ui-components/accordion';
import { initFeaturesListIndexPage } from './featuresListCardIndexPage';
//import { initializeSupportHeroAnimation } from './supportHeroAnimation';

/**
 * Registry of eager initializers keyed by the `data-lazy-init` attribute value.
 *
 * Tabs, accordion, and features-list must be ready the instant the page loads, not
 * just when scrolled into view: the browser's native Find-in-Page can reveal a
 * `hidden` panel anywhere in the document (even off-screen, unscrolled) before an
 * IntersectionObserver would ever fire for it. If that happens before the
 * component is initialized, no `beforematch` listener is attached to sync the
 * reveal — for accordion/tabs the on-init logic (auto-opening the first item /
 * activating the first tab) then runs afterward and stomps over whatever the
 * browser just revealed; for features-list, nothing ever adds the `.active` class
 * that drives the hover-content's opacity, so the browser finds and reports the
 * match (e.g. "1/1") but the panel never visually appears. All three modules
 * already ship in the main bundle, so eager init costs zero extra network
 * round-trips — only a few synchronous DOM queries per instance.
 */
const eagerInitializers = {
	'tabs': initTabs,
	'accordion': initializeAccordion,
	'features-list': initFeaturesListIndexPage,
};

/**
 * Registry of lazy initializers keyed by the `data-lazy-init` attribute value.
 *
 * Sliders use dynamic imports so Swiper JS + CSS only load when the slider
 * enters the viewport — keeps Swiper out of the render-blocking CSS bundle.
 */
const lazyInitializers = {
	'slider-categories': (el) => import(/* webpackChunkName: "slider-categories" */ './slider-category').then(m => m.initSliderCategories(el)),
	'slider-themes':     (el) => import(/* webpackChunkName: "slider-themes" */     './slider-demo-themes').then(m => m.initSliderThemes(el)),
	'slider-video-reviews': (el) => import(/* webpackChunkName: "slider-video-reviews" */ './slider-video-reviews').then(m => m.initSliderVideoReviews(el)),
	//'support-hero': initializeSupportHeroAnimation,
};

/**
 * Finds all elements whose `data-lazy-init` type is in `eagerInitializers` and
 * initializes them immediately, synchronously — see the registry comment above.
 */
export function initEagerComponents() {
	Object.keys(eagerInitializers).forEach(initType => {
		document.querySelectorAll(`[data-lazy-init="${initType}"]`).forEach(element => {
			eagerInitializers[initType](element);
		});
	});
}

/**
 * Finds all elements with a `data-lazy-init` type in `lazyInitializers` and sets
 * up an IntersectionObserver to initialize each one the first time it approaches
 * the viewport.
 *
 * Each element is only initialized once — the observer is disconnected from it
 * immediately after the initializer runs.
 */
export function initLazyComponents() {
	const selector = Object.keys(lazyInitializers).map(type => `[data-lazy-init="${type}"]`).join(',');
	const lazyElements = document.querySelectorAll(selector);

	if (!lazyElements.length) return;

	const observerOptions = {
		root: null,          // viewport
		rootMargin: '100px', // trigger 100px before the element enters the viewport
		threshold: 0         // fire as soon as any pixel is visible
	};

	const lazyObserver = new IntersectionObserver((entries, observer) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				const elementToInit = entry.target;
				const initType = elementToInit.dataset.lazyInit;

				if (typeof lazyInitializers[initType] === 'function') {
					// console.log(`Lazy initializing: ${initType}`, elementToInit);
					lazyInitializers[initType](elementToInit);
				} else {
					console.warn(`No lazy initializer found for type: "${initType}"`);
				}

				// Unobserve immediately — each element must only be initialized once.
				observer.unobserve(elementToInit);
			}
		});
	}, observerOptions);

	lazyElements.forEach(element => {
		lazyObserver.observe(element);
	});
}
